import { createSlice } from "@reduxjs/toolkit";
import * as CONST from "../../app/constants";
import { setLoginExpired } from "./nsLoginSlice";
import { processApplicationResponse } from "./collectionApplicationSlice";
import {
  setServerRequestStatus,
  IsInternalId,
  setAgencyApplicationStatus,
  setAgencyApplicationLocked
} from "./primaryAgencyApplicationSlice";
import {
  removeAdditionalBranchApplicationWithId,
  removeAgentApplicationWithId,
  setCurrentAgentApplicationId,
  setCurrentAgentApplicationLocked,
  setCurrentAgentApplicationStatus
} from "./additionalApplicationsSlice";
import {
  applicationTypeFromLicenceSubType,
  isAgencyTypeApplication,
  isIndividualTypeApplication
} from "./applicationsUtils";
import { fetchApplicationWithId, setFetchQueueRequestStatus } from "./bltHomeSlice";

const initialState = () => ({
  licencingSubType: "COLLECTION_AGENCY_APPLICATION",
  serverRequestStatus: "",
  // submittedDate was originally inside agency application JSON , but we need
  // capability to sort the admin UI queue with this param, so its brought out of
  // the JSON & put in the root DB object.
  submittedDate: 0,
  markedDeleted: false,
  applicationsList: []
});

const businessLicenceApplicationSlice = createSlice({
  name: "businessLicenceApplication",
  initialState: initialState(),
  reducers: {
    setLicencingSubTypeDeprecated(state, action) {
      state.licencingSubType = action.payload;
    },
    setApplicationIdDeprecated(state, action) {
      state.id = action.payload;
    },
    setSaveApplicationDataStatusDeprecated(state, action) {
      state.serverRequestStatus = action.payload;
    },
    saveNextUrlDeprecated(sate, action) {
      window.sessionStorage.setItem("nextUrl", action.payload);
    },
    resetNextUrlDeprecated(state, action) {
      window.sessionStorage.removeItem("nextUrl");
    },
    saveSegueDetailsDeprecated(state, action) {
      console.log(JSON.stringify(action.payload));
      window.sessionStorage.setItem("segue", JSON.stringify(action.payload));
    },
    resetSegueDetailsDeprecated(state, action) {
      window.sessionStorage.removeItem("segue");
    },
    scrollWindowDownDeprecated(state, action) {
      window.scrollTo(0, 999);
    },
    setAccountInfoRequestStatusDeprecated(state, action) {
      state.accountInfoRequestStatus = action.payload;
    },
    setSubmittedDateDeprecated(state, action) {
      state.submittedDate = action.payload;
    },
    setMarkedDeleteDeprecated(state, action) {
      state.markedDeleted = action.payload;
    },
    initialiseAgencyApplicationDeprecated(state, action) {
      state.id = 0;
    },
    setApplicationsListDeprecated(state, action) {
      state.applicationsList = [...action.payload];
    },
    setBusinessApplicationDataFromBLQueueItemDeprecated(state, action) {
      const queueItem = action.payload;

      if (queueItem) {
        state.licencingSubType = queueItem.licencingSubType;
        state.submittedDate = queueItem.submittedDate;

        switch (queueItem.licencingSubType) {
          case CONST.LICENCE_SUB_TYPE_COLLECTION_AGENCY_APPLICATION:
          case CONST.LICENCE_SUB_TYPE_DEBT_MANAGEMENT_APPLICATION:
            state.id = queueItem.id;
            break;
          case CONST.LICENCE_SUB_TYPE_COLLECTION_COLLECTOR_APPLICATION:
          case CONST.LICENCE_SUB_TYPE_DEBT_MANAGEMENT_INDIVIDUAL_APPLICATION:
            // the id should be set to the parent application.
            // this is needed, so that saving the application can send proper ids in the request
            state.id = queueItem.businessLicenceData.businessApplicationId;
            break;
          default:
            state.id = queueItem.id;
            break;
        }
      }
    }
  }
});

export const {
  setLicencingSubTypeDeprecated,
  setApplicationIdDeprecated,
  setSaveApplicationDataStatusDeprecated,
  saveNextUrlDeprecated,
  resetNextUrlDeprecated,
  saveSegueDetailsDeprecated,
  resetSegueDetailsDeprecated,
  scrollWindowDownDeprecated,
  setAccountInfoRequestStatusDeprecated,
  setSubmittedDateDeprecated,
  setMarkedDeleteDeprecated,
  initialiseAgencyApplicationDeprecated,
  setBusinessApplicationDataFromBLQueueItemDeprecated,
  setApplicationsListDeprecated
} = businessLicenceApplicationSlice.actions;

export const approveAndCancelApplicationData =
  (newApplication, oldApplication) => async (dispatch, getState) => {
    if (!newApplication || !oldApplication) {
      return;
    }

    const userAuth = getState().userAuth;

    dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.IN_PROGRESS));

    let headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("X-CSRF-TOKEN", userAuth?.csrfToken);

    return fetch(CONST.APPROVE_AND_CANCEL_APPLICATION_URL, {
      method: "POST",
      headers: headers,
      body: JSON.stringify({
        sourceApplication: oldApplication,
        targetApplication: newApplication
      })
    })
      .then((resp) => {
        // Try to handle as many error codes as possible with its
        // logical resolution.

        if (resp.status !== 200) {
          if (resp.status === 404) {
            // let the flow in local environment go-on.
            if (window.location.hostname === "localhost") {
              dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.SUCCESS));
              return Promise.resolve(CONST.ERROR_CODE.NO_ERROR);
            }
            dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.ERROR));
            return Promise.reject(CONST.ERROR_CODE.UNKNOWN_URL);
          } else if (resp.status === 401 || resp.status === 403) {
            dispatch(setLoginExpired(true));
            dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.ERROR));
            return Promise.reject(CONST.ERROR_CODE.UNAUTHORISED_ACCESS);
          } else {
            dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.ERROR));
            return Promise.reject(CONST.ERROR_CODE.SERVER_ERROR);
          }
        }

        dispatch(setLoginExpired(false));
        return resp.json();
      })
      .then((responseItem) => {
        //console.log(data)
        let primaryApplication = null;
        if (responseItem && responseItem.id) {
          primaryApplication = processApplicationResponse([responseItem], dispatch);
        }

        dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.SUCCESS));

        return primaryApplication;
      })
      .catch((err) => {
        if (window.location.hostname === "localhost") {
          dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.SUCCESS));
          return Promise.resolve(CONST.ERROR_CODE.NO_ERROR);
        }
        dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.ERROR));
        return Promise.reject(CONST.ERROR_CODE.SERVER_ERROR);
      });
  };

/*
 * At a time only one application data will be sent.
 * the data can be for :
 *      - collection / debt management agency
 *      - collector / debt management agent
 *      - additional branch
 */
export const saveBusinessLicenceApplicationData =
  (applicationType, _applicationMode, branchesOnly, agencyChangeType) =>
  async (dispatch, getState) => {
    const agencyApplication = getState().primaryAgencyApplication.application;
    const additionalApplications = getState().additionalApplications;

    const businessLicenceList = [];

    const userAuth = getState().userAuth;

    const removeDocumentId = (d) => {
      if (IsInternalId(d.id)) {
        let docObj = { ...d, id: null };
        if (docObj.fileSize) {
          delete docObj.fileSize;
        }

        return docObj;
      }

      return d;
    };

    const addAgencyApplicationToPayload = () => {
      if (!branchesOnly && agencyApplication) {
        const docs = agencyApplication.documents?.map(removeDocumentId);
        const application = { ...agencyApplication, documents: docs };

        if (IsInternalId(application.id)) {
          businessLicenceList.push({
            ...application,
            id: null,
            name: application.businessLicenceData.businessName
          });
        } else {
          businessLicenceList.push({
            ...application,
            changeType: (agencyChangeType === "UPDATE") ? "UPDATE" : "SAVE"
          });
        }
      }

      // always sync the status of addition branch applications with agency application (except specific update)
      if (agencyChangeType !== "UPDATE") {
        additionalApplications.additionalBranchApplications.forEach((branchApplication) => {
          const docs = branchApplication?.documents?.map(removeDocumentId) ?? [];
          const application = { ...branchApplication, documents: docs };

          if (IsInternalId(application.id)) {
            businessLicenceList.push({
              ...application,
              id: null,
              businessApplicationId: agencyApplication.id,
              name: agencyApplication.businessLicenceData.businessName
            });
          } else {
            businessLicenceList.push({
              ...application,
              changeType: application.changeType ?? "SAVE"
            });
          }
        });
      }
    };

    const addAgentApplicationsToPayload = () => {
      const agentApplication = additionalApplications.agentApplications.find(
        (app) => app.id === additionalApplications.currentAgentApplicationId
      );

      if (agentApplication) {
        const docs = agentApplication?.documents?.map(removeDocumentId);
        const application = { ...agentApplication, documents: docs };

        if (IsInternalId(application.id)) {
          businessLicenceList.push({
            ...application,
            id: null
          });
        } else {
          businessLicenceList.push({
            ...application,
            changeType: application.changeType ?? "SAVE"
          });
        }
      }
    };

    switch (applicationType) {
      case CONST.APPLICATION_TYPE_COLLECTION_AGENCY:
      case CONST.APPLICATION_TYPE_DEBT_MANAGEMENT_AGENCY:
      case CONST.APPLICATION_TYPE_MORTGAGE_BROKERAGE_LICENCE:
      case CONST.APPLICATION_TYPE_MORTGAGE_LENDER_LICENCE:
      case CONST.APPLICATION_TYPE_MORTGAGE_ADMINISTRATOR_LICENCE:
      case CONST.APPLICATION_TYPE_INDIVIDUAL_COMPLIANCE_OFFICER_LICENCE:
      case CONST.APPLICATION_TYPE_INDIVIDUAL_PRINCIPAL_BROKER_LICENCE:
      case CONST.APPLICATION_TYPE_INDIVIDUAL_PRINCIPAL_BROKER_RETENTION_LICENCE:
      case CONST.APPLICATION_TYPE_INDIVIDUAL_COMPLIANCE_OFFICER_RETENTION_LICENCE:
        addAgencyApplicationToPayload();
        break;
      case CONST.APPLICATION_TYPE_COLLECTOR:
      case CONST.APPLICATION_TYPE_DEBT_MANAGEMENT_AGENT:
      case CONST.APPLICATION_TYPE_MORTGAGE_PRINCIPAL_BROKER_LICENCE:
      case CONST.APPLICATION_TYPE_MORTGAGE_INDIVIDUAL_BROKER_LICENCE:
      case CONST.APPLICATION_TYPE_MORTGAGE_ASSOCIATE_BROKER_LICENCE:
        addAgentApplicationsToPayload();
        break;
      default:
        console.error("unknown applicationType - " + applicationType);
        break;
    }

    dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.IN_PROGRESS));

    let headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("X-CSRF-TOKEN", userAuth?.csrfToken);

    //console.log(JSON.stringify(applicationData))

    return fetch(CONST.SAVE_APPLICATION_DATA_URL, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(businessLicenceList)
    })
      .then((resp) => {
        // Try to handle as many error codes as possible with its
        // logical resolution.

        if (resp.status !== 200) {
          if (resp.status === 404) {
            // let the flow in local environment go-on.
            if (window.location.hostname === "localhost") {
              dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.SUCCESS));
              return Promise.resolve(CONST.ERROR_CODE.NO_ERROR);
            }
            dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.ERROR));
            return Promise.reject(CONST.ERROR_CODE.UNKNOWN_URL);
          } else if (resp.status === 401 || resp.status === 403) {
            dispatch(setLoginExpired(true));
            dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.ERROR));
            return Promise.reject(CONST.ERROR_CODE.UNAUTHORISED_ACCESS);
          } else {
            dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.ERROR));
            return Promise.reject(CONST.ERROR_CODE.SERVER_ERROR);
          }
        }

        dispatch(setLoginExpired(false));
        return resp.json();
      })
      .then((responseList) => {
        //console.log(data)
        let primaryApplication = null;
        if (responseList && Array.isArray(responseList) && responseList.length > 0) {
          switch (applicationType) {
            case CONST.APPLICATION_TYPE_COLLECTION_AGENCY:
            case CONST.APPLICATION_TYPE_DEBT_MANAGEMENT_AGENCY:
            case CONST.APPLICATION_TYPE_MORTGAGE_BROKERAGE_LICENCE:
            case CONST.APPLICATION_TYPE_MORTGAGE_LENDER_LICENCE:
            case CONST.APPLICATION_TYPE_MORTGAGE_ADMINISTRATOR_LICENCE:
              // always sync the status of addition branch applications with agency application
              additionalApplications.additionalBranchApplications.forEach((branchApplication) => {
                // hopefully the additional branch application is saved successfully
                if (IsInternalId(branchApplication.id)) {
                  dispatch(removeAdditionalBranchApplicationWithId(branchApplication.id));
                }
              });
              break;
            case CONST.APPLICATION_TYPE_COLLECTOR:
            case CONST.APPLICATION_TYPE_DEBT_MANAGEMENT_AGENT:
            case CONST.APPLICATION_TYPE_MORTGAGE_PRINCIPAL_BROKER_LICENCE:
            case CONST.APPLICATION_TYPE_MORTGAGE_INDIVIDUAL_BROKER_LICENCE:
            case CONST.APPLICATION_TYPE_MORTGAGE_ASSOCIATE_BROKER_LICENCE:
              const agentApplication = additionalApplications.agentApplications.find(
                (app) => app.id === additionalApplications.currentAgentApplicationId
              );

              // hopefully the agent application is saved successfully
              if (agentApplication) {
                if (IsInternalId(agentApplication.id)) {
                  dispatch(removeAgentApplicationWithId(agentApplication.id));
                }
              }
              break;
            default:
              console.error("unknown applicationType - " + applicationType);
              break;
          }

          primaryApplication = processApplicationResponse(responseList, dispatch);

          if (
            applicationType === CONST.APPLICATION_TYPE_COLLECTOR ||
            applicationType === CONST.APPLICATION_TYPE_DEBT_MANAGEMENT_AGENT ||
            applicationType === CONST.APPLICATION_TYPE_MORTGAGE_PRINCIPAL_BROKER_LICENCE ||
            applicationType === CONST.APPLICATION_TYPE_MORTGAGE_INDIVIDUAL_BROKER_LICENCE ||
            applicationType === CONST.APPLICATION_TYPE_MORTGAGE_ASSOCIATE_BROKER_LICENCE ||
            applicationType === CONST.APPLICATION_TYPE_MORTGAGE_LENDER_LICENCE
          ) {
            const agentApp = responseList.find(isIndividualTypeApplication);

            if (agentApp) {
              dispatch(setCurrentAgentApplicationId(agentApp.id));
            }
          }
        }

        dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.SUCCESS));

        return primaryApplication;
      })
      .catch((err) => {
        if (window.location.hostname === "localhost") {
          dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.SUCCESS));
          return Promise.resolve(CONST.ERROR_CODE.NO_ERROR);
        }

        dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.ERROR));
        return Promise.reject(CONST.ERROR_CODE.SERVER_ERROR);
      });
  };

/*
 * renew individual applications.
 */
export const syncRjscDetailsForIndividualApplications =
  (agentApplicationIds) => async (dispatch, getState) => {
    const additionalApplications = getState().additionalApplications;

    const agentApplicationsToPost = [];

    agentApplicationIds.forEach((appId) => {
      const index = additionalApplications.agentApplications.findIndex(
        (agentApp) => agentApp.id === appId
      );
      if (index >= 0) {
        const agentApplication = additionalApplications.agentApplications[index];
        agentApplicationsToPost.push({ ...agentApplication, changeType: "SAVE" });
      }
    });

    const { userAuth } = getState();
    const { csrfToken } = userAuth;

    let headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("X-CSRF-TOKEN", csrfToken);

    return fetch(CONST.SYNC_RJSC_DETAILS_FOR_INDIVIDUAL_APPLICATIONS, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(agentApplicationsToPost)
    })
      .then((resp) => resp.json())
      .then((data) => {
        const { businessLicenceList } = data;

        if (
          businessLicenceList &&
          Array.isArray(businessLicenceList) &&
          businessLicenceList.length > 0
        ) {
          processApplicationResponse(businessLicenceList, dispatch);
        } else {
          return Promise.reject(CONST.ERROR_CODE.EMPTY_RESPONSE);
        }

        return data;
      })
      .catch((err) => console.error(err));
  };

/*
 * renew individual applications.
 */
export const renewIndividualApplications = (agentApplicationIds) => async (dispatch, getState) => {
  const additionalApplications = getState().additionalApplications;

  const agentApplicationsToPost = [];

  agentApplicationIds.forEach((appId) => {
    const index = additionalApplications.agentApplications.findIndex(
      (agentApp) => agentApp.id === appId
    );
    if (index >= 0) {
      const agentApplication = additionalApplications.agentApplications[index];
      agentApplicationsToPost.push({ ...agentApplication, changeType: "SAVE" });
    }
  });

  const { userAuth } = getState();
  const { csrfToken } = userAuth;

  let headers = new Headers();
  headers.append("Content-Type", "application/json");
  headers.append("X-CSRF-TOKEN", csrfToken);

  return fetch(CONST.RENEW_INDIVIDUAL_APPLICATIONS, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(agentApplicationsToPost)
  })
    .then((resp) => resp.json())
    .then((data) => {
      const { businessLicenceList } = data;

      if (
        businessLicenceList &&
        Array.isArray(businessLicenceList) &&
        businessLicenceList.length > 0
      ) {
        processApplicationResponse(businessLicenceList, dispatch);
      } else {
        return Promise.reject(CONST.ERROR_CODE.EMPTY_RESPONSE);
      }

      return data;
    })
    .catch((err) => console.error(err));
};

/*
 * do not renew individual applications.
 */
export const doNotRenewIndividualApplications =
  (agentApplicationIds) => async (dispatch, getState) => {
    const additionalApplications = getState().additionalApplications;

    const agentApplicationsToPost = [];

    agentApplicationIds.forEach((appId) => {
      const index = additionalApplications.agentApplications.findIndex(
        (agentApp) => agentApp.id === appId
      );
      if (index >= 0) {
        const agentApplication = additionalApplications.agentApplications[index];
        agentApplicationsToPost.push({ ...agentApplication, changeType: "SAVE" });
      }
    });

    const { userAuth } = getState();
    const { csrfToken } = userAuth;

    let headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("X-CSRF-TOKEN", csrfToken);

    return fetch(CONST.DO_NOT_RENEW_INIVIDUAL_APPLICATIONS, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(agentApplicationsToPost)
    })
      .then((resp) => resp.json())
      .then((data) => {
        const { businessLicenceList } = data;

        if (
          businessLicenceList &&
          Array.isArray(businessLicenceList) &&
          businessLicenceList.length > 0
        ) {
          processApplicationResponse(businessLicenceList, dispatch);
        } else {
          return Promise.reject(CONST.ERROR_CODE.EMPTY_RESPONSE);
        }

        return data;
      })
      .catch((err) => console.error(err));
  };

/*
 * do not renew individual applications.
 */
export const markDeleteAllApplications = () => async (dispatch, getState) => {
  const { application } = getState().primaryAgencyApplication;
  const { additionalBranchApplications, agentApplications } = getState().additionalApplications;

  let applicationsPayload = [];
  applicationsPayload.push({ ...application });
  applicationsPayload = [
    ...applicationsPayload,
    ...additionalBranchApplications,
    ...agentApplications
  ];

  const { userAuth } = getState();
  const { csrfToken } = userAuth;

  let headers = new Headers();
  headers.append("Content-Type", "application/json");
  headers.append("X-CSRF-TOKEN", csrfToken);

  return fetch(CONST.MARK_DELETE_APPLICATIONS, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(applicationsPayload)
  })
    .then((resp) => resp.json())
    .then((data) => {
      const { businessLicenceList } = data;

      if (
        businessLicenceList &&
        Array.isArray(businessLicenceList) &&
        businessLicenceList.length > 0
      ) {
        processApplicationResponse(businessLicenceList, dispatch);
      } else {
        return Promise.reject(CONST.ERROR_CODE.EMPTY_RESPONSE);
      }

      return data;
    })
    .catch((err) => console.error(err));
};

export const markDeleteApplications = (applications) => async (dispatch, getState) => {
  const { userAuth } = getState();
  const { csrfToken } = userAuth;

  let headers = new Headers();
  headers.append("Content-Type", "application/json");
  headers.append("X-CSRF-TOKEN", csrfToken);

  return fetch(CONST.MARK_DELETE_APPLICATIONS, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(applications)
  })
    .then((resp) => resp.json())
    .then((data) => {
      const { businessLicenceList } = data;

      if (
        businessLicenceList &&
        Array.isArray(businessLicenceList) &&
        businessLicenceList.length > 0
      ) {
        return data;
      } else {
        return Promise.reject(CONST.ERROR_CODE.EMPTY_RESPONSE);
      }
    })
    .catch((err) => console.error(err));
};

/*
 */
export const saveAgentApplicationWithIds = (agentApplicationIds) => async (dispatch, getState) => {
  const additionalApplications = getState().additionalApplications;

  const agentApplicationsToPost = [];

  agentApplicationIds.forEach((appId) => {
    const index = additionalApplications.agentApplications.findIndex(
      (agentApp) => agentApp.id === appId
    );
    if (index >= 0) {
      const agentApplication = additionalApplications.agentApplications[index];
      agentApplicationsToPost.push({ ...agentApplication, changeType: "SAVE" });
    }
  });

  const { userAuth } = getState();
  const { csrfToken } = userAuth;

  let headers = new Headers();
  headers.append("Content-Type", "application/json");
  headers.append("X-CSRF-TOKEN", csrfToken);

  return fetch(CONST.SAVE_APPLICATION_STATUS_URL, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(agentApplicationsToPost)
  })
    .then((resp) => resp.json())
    .then((data) => {
      processApplicationResponse(data, dispatch);
      return data;
    })
    .catch((err) => console.error(err));
};

/*
 * This function takes the payload as it is and send it to the server with
 * POST request.
 */
export const postApplicationPayload = (applicationPayload) => async (dispatch, getState) => {
  const { csrfToken } = getState().userAuth;

  dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.IN_PROGRESS));

  let headers = new Headers();
  headers.append("Content-Type", "application/json");
  headers.append("X-CSRF-TOKEN", csrfToken);

  //console.log(JSON.stringify(applicationData))

  if (!Array.isArray(applicationPayload)) {
    applicationPayload = [applicationPayload];
  }

  return fetch(CONST.SAVE_APPLICATION_DATA_URL, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(applicationPayload)
  })
    .then((resp) => {
      // check for authentication error.
      // in case of auth error, navigation to the nsLogin screen
      // if(resp.code === 401) { dispatch(push()) }
      if (resp.status === 401 || resp.status === 403) {
        dispatch(setLoginExpired(true));
        dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.ERROR));
        return Promise.reject(CONST.ERROR_CODE.UNAUTHORISED_ACCESS);
      } else if (resp.status === 404) {
        // let the flow in local environment go-on.
        if (window.location.hostname === "localhost") {
          dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.SUCCESS));
          return Promise.resolve(CONST.ERROR_CODE.NO_ERROR);
        }

        dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.ERROR));
        return Promise.reject(CONST.ERROR_CODE.UNKNOWN_URL);
      } else if (resp.status === 500) {
        dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.ERROR));
        return Promise.reject(CONST.ERROR_CODE.SERVER_ERROR);
      }

      dispatch(setLoginExpired(false));
      return resp.json();
    })
    .then((data) => {
      dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.SUCCESS));
      return data;
    });
};

export const unlockApplicationAndResetStatus = () => (dispatch, getState) => {
  const { bltComponents, userAuth, primaryAgencyApplication, additionalApplications } = getState();

  const { bltReview } = bltComponents;
  const { queueItem } = bltReview;
  const { userData } = userAuth;
  const applicationType = applicationTypeFromLicenceSubType(queueItem.licencingSubType);
  const applicationMode = CONST.APPLICATION_MODE_NEW;

  // it could be either agency or individual agent application
  let needToSave = false;
  if (isAgencyTypeApplication(queueItem)) {
    const applicationItem = primaryAgencyApplication?.application;

    if (applicationItem?.locked === userData.email) {
      dispatch(setAgencyApplicationLocked(null));

      // The application status has to be "UNDER_REVIEW", but just make sure
      if (applicationItem?.applicationStatus === CONST.APPLICATION_STATUS_UNDER_REVIEW) {
        dispatch(setAgencyApplicationStatus(CONST.APPLICATION_STATUS_OPENED));
      }

      needToSave = true;
    }
  } else if (isIndividualTypeApplication(queueItem)) {
    if (additionalApplications && additionalApplications.currentAgentApplicationId) {
      const { agentApplications } = additionalApplications;

      const currentAgentApplication = agentApplications.find(
        (app) => additionalApplications.currentAgentApplicationId === app.id
      );
      if (currentAgentApplication?.locked === userData.email) {
        dispatch(setCurrentAgentApplicationLocked(null));

        // The application status has to be "UNDER_REVIEW", but just make sure
        if (currentAgentApplication?.applicationStatus === CONST.APPLICATION_STATUS_UNDER_REVIEW) {
          dispatch(setCurrentAgentApplicationStatus(CONST.APPLICATION_STATUS_OPENED));
        }

        needToSave = true;
      }
    }
  }

  if (needToSave) {
    dispatch(saveBusinessLicenceApplicationData(applicationType, applicationMode));
  }
};

export const fetchLoginSearchResponse = (params) => async (dispatch, getState) => {
  dispatch(setFetchQueueRequestStatus(CONST.REQUEST_STATUS.IN_PROGRESS));

  const mainUrl =
    window.location.hostname === "localhost" ? "/api/get-user-by-email" : CONST.SEARCH_LOGIN_ID_URL;

  const url = `${mainUrl}?email=${params.email}`;

  const userAuth = getState().userAuth;
  let headers = new Headers();
  headers.append("Content-Type", "application/json");
  headers.append("X-CSRF-TOKEN", userAuth?.csrfToken);

  const resp = await fetch(url, {
    headers
  });

  if (resp.status === 401 || resp.status === 403) {
    console.log(resp);
    dispatch(setFetchQueueRequestStatus(CONST.REQUEST_STATUS.ERROR));
    return Promise.reject(CONST.ERROR_CODE.UNAUTHORISED_ACCESS);
  }

  const data = await resp.json();
  return data;
};

/** function for API call to change agency login id */
export const saveNewLoginInAgencyApplication = (searchedUser) => async (dispatch, getState) => {
  const agencyApplication = getState().primaryAgencyApplication.application;
  const userAuth = getState().userAuth;

  dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.IN_PROGRESS));

  let headers = new Headers();
  headers.append("Content-Type", "application/json");
  headers.append("X-CSRF-TOKEN", userAuth?.csrfToken);

  const isMortgageBrokerage =
    agencyApplication.licencingSubType ===
    CONST.LICENCE_SUB_TYPE_MORTGAGE_BROKERAGE_NEW_APPLICATION;

  const reqURL = isMortgageBrokerage
    ? CONST.CHANGE_PRINCIPAL_BROKER_URL
    : CONST.CHANGE_COMPLIANCE_OFFICER_URL;

  const urlWithParams = `${reqURL}?businessApplicationId=${agencyApplication.id}&rpid=${searchedUser.rpid}`;

  return fetch(urlWithParams, {
    method: "POST",
    headers: headers
    // body: JSON.stringify(bodyData)
  })
    .then((resp) => {
      if (resp.status !== 200) {
        if (resp.status === 404) {
          if (window.location.hostname === "localhost") {
            dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.SUCCESS));
            return Promise.resolve(CONST.ERROR_CODE.NO_ERROR);
          }
          dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.ERROR));
          return Promise.reject(CONST.ERROR_CODE.UNKNOWN_URL);
        } else if (resp.status === 401 || resp.status === 403) {
          dispatch(setLoginExpired(true));
          dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.ERROR));
          return Promise.reject(CONST.ERROR_CODE.UNAUTHORISED_ACCESS);
        } else {
          dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.ERROR));
          return Promise.reject(CONST.ERROR_CODE.SERVER_ERROR);
        }
      }

      dispatch(setLoginExpired(false));
      return resp.json();
    })
    .then((responseData) => {
      dispatch(fetchApplicationWithId(agencyApplication.id)).then((updatedApplication) => {
        if (updatedApplication?.id === agencyApplication.id) {
          const parsedApp = processApplicationResponse([updatedApplication], dispatch);
          console.log(parsedApp);
        }
      });

      dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.SUCCESS));
      return responseData;
    })
    .catch((err) => {
      if (window.location.hostname === "localhost") {
        dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.SUCCESS));
        return Promise.resolve(CONST.ERROR_CODE.NO_ERROR);
      }

      console.log(err);

      dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.ERROR));
      return Promise.reject(CONST.ERROR_CODE.SERVER_ERROR);
    });
};

/*
export const submitBusinessLicenceApplicationForBLReview =
  (applicationType, _applicationMode, branchesOnly) => async (dispatch, getState) => {
    const agencyApplication = getState().primaryAgencyApplication.application;
    const businessLicenceList = [];

    const userAuth = getState().userAuth;

    const removeDocumentId = (d) => {
      if (IsInternalId(d.id)) {
        let docObj = { ...d, id: null }
        if (docObj.fileSize) {
          delete docObj.fileSize
        }

        return docObj
      }

      return d;
    }

    const addAgencyApplicationToPayload = () => {
      if (!branchesOnly && agencyApplication) {
        const docs = agencyApplication.documents?.map(removeDocumentId)
        const application = { ...agencyApplication, documents: docs }

        if (IsInternalId(application.id)) {
          businessLicenceList.push({
            ...application,
            id: null,
            name: application.businessLicenceData.businessName,
          });
        } else {
          businessLicenceList.push({
            ...application,
            changeType: "SAVE",
          });
        }
      }
    };

    switch (applicationType) {
      case CONST.APPLICATION_TYPE_INDIVIDUAL_COMPLIANCE_OFFICER_LICENCE:
      case CONST.APPLICATION_TYPE_INDIVIDUAL_PRINCIPAL_BROKER_LICENCE:
      case CONST.APPLICATION_TYPE_INDIVIDUAL_PRINCIPAL_BROKER_RETENTION_LICENCE:
      case CONST.APPLICATION_TYPE_INDIVIDUAL_COMPLIANCE_OFFICER_RETENTION_LICENCE:
        addAgencyApplicationToPayload();
        break;
     
      default:
        console.error("unknown applicationType - " + applicationType);
        break;
    }

    dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.IN_PROGRESS));

    let headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("X-CSRF-TOKEN", userAuth?.csrfToken);

    return fetch(CONST.SAVE_APPLICATION_DATA_URL, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(businessLicenceList),
    })
      .then((resp) => {

        // Try to handle as many error codes as possible with its
        // logical resolution.

        if (resp.status !== 200) {
          if (resp.status === 404) {
            // let the flow in local environment go-on.
            if (window.location.hostname === "localhost") {
              dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.SUCCESS));
              return Promise.resolve(CONST.ERROR_CODE.NO_ERROR);
            }
            dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.ERROR));
            return Promise.reject(CONST.ERROR_CODE.UNKNOWN_URL);
          } else if (resp.status === 401 || resp.status === 403) {
            dispatch(setLoginExpired(true));
            dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.ERROR));
            return Promise.reject(CONST.ERROR_CODE.UNAUTHORISED_ACCESS);
          } else {
            dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.ERROR));
            return Promise.reject(CONST.ERROR_CODE.SERVER_ERROR);
          }
        }

        dispatch(setLoginExpired(false));
        return resp.json();
      })
      .then((responseList) => {
        //console.log(data)
        let primaryApplication = null;
        if (
          responseList &&
          Array.isArray(responseList) &&
          responseList.length > 0
        ) {
          primaryApplication = processApplicationResponse(
            responseList,
            dispatch
          ); 
        }

        dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.SUCCESS));

        return primaryApplication;
      })
      .catch((err) => {
        if (window.location.hostname === "localhost") {
          dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.SUCCESS));
          return Promise.resolve(CONST.ERROR_CODE.NO_ERROR);
        }

        dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.ERROR));
        return Promise.reject(CONST.ERROR_CODE.SERVER_ERROR);
      })
  };
  */

  export const checkLicenceNumberActive =
  (licenceNumber) => async (dispatch, getState) => {
    if (!licenceNumber) {
      return;
    }

    const userAuth = getState().userAuth;

    dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.IN_PROGRESS));

    let headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("X-CSRF-TOKEN", userAuth?.csrfToken);

    return fetch(CONST.CHECK_LICENCE_ACTIVE_URL + "?licenceNumber=" + licenceNumber, {
      method: "GET",
      headers: headers
    })
      .then((resp) => {
        // Try to handle as many error codes as possible with its
        // logical resolution.

        if (resp.status !== 200) {
          if (resp.status === 404) {
            // let the flow in local environment go-on.
            if (window.location.hostname === "localhost") {
              dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.SUCCESS));
              return Promise.resolve(CONST.ERROR_CODE.NO_ERROR);
            }
            dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.ERROR));
            return Promise.reject(CONST.ERROR_CODE.UNKNOWN_URL);
          } else if (resp.status === 401 || resp.status === 403) {
            dispatch(setLoginExpired(true));
            dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.ERROR));
            return Promise.reject(CONST.ERROR_CODE.UNAUTHORISED_ACCESS);
          } else {
            dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.ERROR));
            return Promise.reject(CONST.ERROR_CODE.SERVER_ERROR);
          }
        }

        dispatch(setLoginExpired(false));
        return resp.json();
      })
      .then((responseData) => {
        console.log(responseData)
       

        dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.SUCCESS));

        return responseData;
      })
      .catch((err) => {

        if (window.location.hostname === "localhost") {
          dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.SUCCESS));
          return Promise.resolve(CONST.ERROR_CODE.NO_ERROR);
        }
        dispatch(setServerRequestStatus(CONST.REQUEST_STATUS.ERROR));
        return Promise.reject(CONST.ERROR_CODE.SERVER_ERROR);
      });
  };

export default businessLicenceApplicationSlice.reducer;
